import React, { useEffect, useRef } from 'react';
import {
  useClustersForQuestion,
  clusteringTestDataOptions,
} from '@mentimeter/core-hooks';
import { getGroupsShapes } from '@mentimeter/slides-presentation-ui/groups/group-shapes';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useTranslations } from 'next-intl';
import { trackUser } from '@api/tracking/client';
import { usePresentationContext } from '../../../../../legacy-context/presentationContext';
import { createUngroupedGroup } from '../../utils/createUngroupedGroup';
import { UNGROUPED_CLUSTER_ID } from '../../../groups';
import type { SlideStateOpen } from '../../../slide-state';
import { testDataClustering } from '../utils/testData';
import { GroupsWithoutVotesView } from './GroupsWithoutVotesView';

export const GroupsWithoutVotesDataHandler = () => {
  const { isPreviewResultsEnabled, useSlideState, useQuestion } =
    usePresentationContext();

  const translate = useTranslations('presenting.legacy');
  const {
    theme: {
      visualizationColors: { fillColors },
    },
  } = useRagnar();

  const { slideAdminKey: admin_key, series_id, hide_results } = useQuestion();

  const {
    clusteringForQuestion,
    isLoading,
    clusteringForQuestionError,
    maxAttemptsReached,
    regenerate,
    hasExistingClusters,
  } = useClustersForQuestion({
    shouldFetch: !isPreviewResultsEnabled,
    slideAdminKey: admin_key,
    testVotesShowing: isPreviewResultsEnabled,
    options: isPreviewResultsEnabled
      ? {
          ...clusteringTestDataOptions,
          fallbackData: testDataClustering,
        }
      : {},
  });
  const { status } = clusteringForQuestion ?? {};

  const [{ shouldShowGroups }, setSlideState] = useSlideState<SlideStateOpen>();

  const aiDisabledError =
    clusteringForQuestionError?.response?.data?.code === 'ai_disabled';
  const hasError = clusteringForQuestionError || maxAttemptsReached;

  const errorType = (() => {
    if (aiDisabledError) {
      return 'ai-disabled';
    }
    if (hasError) {
      return 'something-went-wrong';
    }
    return undefined;
  })();

  const isTrackingPerformed = useRef(false);

  const groups = clusteringForQuestion?.clusters ?? [];
  const ungroupedResponses = clusteringForQuestion?.unclustered_responses ?? [];

  const ungroupedGroup = createUngroupedGroup({
    clusteringId: groups[0]?.clustering_id,
    ungroupedResponses,
    label: translate('open.new_responses'),
  });

  const groupsWithUngrouped = ungroupedGroup
    ? [...groups, ungroupedGroup]
    : groups;

  const groupShapeMap = getGroupsShapes(
    groupsWithUngrouped,
    fillColors,
    UNGROUPED_CLUSTER_ID,
  );

  /*   To get back to initial slide state when toggling of test votes */
  if (
    !isPreviewResultsEnabled &&
    shouldShowGroups &&
    !groups.length &&
    !ungroupedResponses.length &&
    !isLoading &&
    !hasError
  ) {
    setSlideState({ shouldShowGroups: false });
  }
  const onBackToResponses = () => {
    setSlideState({ shouldShowGroups: false });
  };

  useEffect(() => {
    if (status && !isTrackingPerformed.current) {
      trackUser({
        event: 'Used open ended clustering',
        properties: {
          context: 'Presentation view',
          'existing clusters': hasExistingClusters.current,
          'test votes': isPreviewResultsEnabled,
          'series id': series_id,
        },
      });
      isTrackingPerformed.current = true;
    }
  }, [
    isTrackingPerformed,
    status,
    isPreviewResultsEnabled,
    series_id,
    hasExistingClusters,
  ]);

  const onRegenerate = () => {
    regenerate();
  };

  return (
    <GroupsWithoutVotesView
      shouldHideResponses={hide_results}
      error={errorType}
      isLoading={isLoading}
      groups={groupsWithUngrouped}
      groupShapeMap={groupShapeMap}
      onRegenerate={onRegenerate}
      onBackToResponses={onBackToResponses}
      clusteringId={clusteringForQuestion?.id}
    />
  );
};
